import * as React from "react"
import type { ConsumerInfoType, SubmitConsumerInfoType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import Cookies from "js-cookie"
import { Maximize, Minimize } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { MeterInfoType } from "@/types/meter"
import {
  getBarangays,
  getMunicipalities,
  getProvinces,
  getRegions,
} from "@/lib/api/address"
import {
  addConsumer,
  editConsumer,
  getBooksZone,
  getConnectionStatus,
  getConnectionType,
  getMeterReaderDropdown,
  getZoneDropdown,
} from "@/lib/api/billing"
import { getUnassignedMeters } from "@/lib/api/meter"
import { docsRequirements, validIds } from "@/lib/mock-data"
import { consumerSchema } from "@/lib/schema/consumer"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { CalendarSingle } from "@/components/ui/calendar-single"
import { Card } from "@/components/ui/card"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ComboboxSearch } from "@/components/combobox-search"
import { Icons } from "@/components/icons"
import { MapRenderer } from "@/components/map/map-renderer"

import profilePlaceholder from "../assets/photo-placeholder.svg"
import { Checkbox } from "../ui/checkbox"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea"

interface Profile {
  image: string | File
}

export function ConsumerForm({ editData }: { editData?: ConsumerInfoType }) {
  const client = Cookies.get("user_client_info") || "{}"
  const location = {
    lat: editData?.lat || JSON.parse(client).lat,
    lon: editData?.lon || JSON.parse(client).lon,
  }

  const [maximizeMap, setMaximizeMap] = React.useState(false)

  const [zoneId, setZoneId] = React.useState<number | undefined>(
    editData?.zone_id,
  )
  const [regionId, setRegionId] = React.useState<number | undefined>(
    editData?.region,
  )
  const [provinceId, setProvinceId] = React.useState<number | undefined>(
    editData?.province,
  )
  const [municipalityId, setMunicipalityId] = React.useState<
    number | undefined
  >(editData?.municipality)
  const clientId = JSON.parse(client).client_id

  const userId = Cookies.get("user_id")
  const { toast } = useToast()
  const navigate = useNavigate()
  const isConnectionTypeDropdown = true
  const queryClient = useQueryClient()

  /*===============================================================
                       Applicant Profile Picture
  =================================================================*/
  const [profile, setProfile] = React.useState<Profile>({
    image: "",
  })

  const [file, setFile] = React.useState<string | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(URL.createObjectURL(selectedFile))
    } else {
      // Handle the case where no file is selected
      setFile(null)
    }
  }

  console.log(file)
  /*===============================================================
                          Fetch Selected Files
  =================================================================*/
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([])
  const [selectedValidIDFiles, setSelectedValidIDFiles] = React.useState<
    File[]
  >([])
  const [requiredFileAlert, setRequiredFileAlert] = React.useState(false)

  // Maximum number of files allowed
  const maxFiles = 1
  // Allowed file types
  const allowedFileTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/png",
    "image/jpeg",
  ]

  // Generalized file handling function
  const handleFileAction = (
    filesArray: File[],
    setSelectedFilesFunc: React.Dispatch<React.SetStateAction<File[]>>,
  ) => {
    setRequiredFileAlert(false)
    const currentLength =
      setSelectedFilesFunc === setSelectedValidIDFiles
        ? selectedValidIDFiles.length
        : selectedFiles.length

    // Check if adding these files exceeds the max limit
    if (filesArray.length + currentLength > maxFiles) {
      toast({
        description: "You have reached the maximum number of file uploads.",
        variant: "destructive",
      })

      return // Prevent adding more files
    }

    // Validate file types
    const invalidFiles = filesArray.filter(
      (file) => !allowedFileTypes.includes(file.type),
    )
    if (invalidFiles.length > 0) {
      toast({
        description: "Only PDF, DOCX, PNG, JPEG, and JPG files are allowed.",
        variant: "destructive",
      })

      return // Prevent adding invalid files
    }

    setSelectedFilesFunc((prevSelectedFiles) => [
      ...prevSelectedFiles,
      ...filesArray,
    ])
  }

  // Handle file selection (for both regular files and valid ID files)
  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    setSelectedFilesFunc: React.Dispatch<React.SetStateAction<File[]>>,
  ) => {
    const input = e.target
    if (!input.files) return

    const filesArray = Array.from(input.files)
    handleFileAction(filesArray, setSelectedFilesFunc) // Add to selected files
  }

  // Handle file drop (for both regular files and valid ID files)
  const handleFileDrop = (
    event: React.DragEvent<HTMLDivElement>,
    setSelectedFilesFunc: React.Dispatch<React.SetStateAction<File[]>>,
  ) => {
    event.preventDefault()
    const filesArray = Array.from(event.dataTransfer.files)
    handleFileAction(filesArray, setSelectedFilesFunc) // Add to selected files
  }

  // Prevent default behavior for drag and drop events
  const preventDefaults = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  // Function to remove a specific file from the selected files list
  const removeSelectedFile = (indexToRemove: number) => {
    setSelectedFiles((prevSelectedFiles) =>
      prevSelectedFiles.filter((_, index) => index !== indexToRemove),
    )
  }

  // Function to remove a specific file from the valid ID files list
  const removeSelectedValidIDFile = (indexToRemove: number) => {
    setSelectedValidIDFiles((prevSelectedFiles) =>
      prevSelectedFiles.filter((_, index) => index !== indexToRemove),
    )
  }

  const queryConfigs = [
    {
      queryKey: ["connectionType"],
      queryFn: () => getConnectionType(isConnectionTypeDropdown),
    },
    { queryKey: ["connectionStatus"], queryFn: getConnectionStatus },
    {
      queryKey: ["zones"],
      queryFn: getZoneDropdown,
    },
    {
      queryKey: ["meterInfo"],
      queryFn: async () => {
        const data = await getUnassignedMeters()
        const meterOptions = data.map((item: MeterInfoType) => ({
          id: item.meter_info_id,
          label: item.serial_number,
          value: item.meter_info_id,
        }))

        if (editData?.meter_info) {
          const assignedMeter = {
            id: editData.meter_info.meter_info_id,
            label: editData.meter_info.serial_number,
            value: editData.meter_info.meter_info_id,
          }

          const exists = meterOptions.some((m) => m.id === assignedMeter.id)
          if (!exists) {
            meterOptions.push(assignedMeter)
          }
        }

        return meterOptions
      },
    },
    {
      queryKey: ["meterReaderZone", zoneId],
      queryFn: () => getMeterReaderDropdown(zoneId!),
    },
    { queryKey: ["booksZone", zoneId], queryFn: () => getBooksZone(zoneId!) },
    { queryKey: ["regions"], queryFn: getRegions },
    /*  {
      queryKey: ["provinces", regionId],
      queryFn: () => getProvinces(regionId!),
    }, */
    {
      queryKey: ["provinces", JSON.parse(client).region.id],
      queryFn: () => getProvinces(JSON.parse(client).region.id!),
    },
    {
      queryKey: ["municipalities", JSON.parse(client).province.id],
      queryFn: () => getMunicipalities(JSON.parse(client).province.id!),
    },
    {
      queryKey: ["barangays", municipalityId],
      queryFn: () => getBarangays(municipalityId!),
    },
  ]

  const results = useQueries({
    queries: queryConfigs,
  })

  const [
    connectionType,
    connectionStatus,
    zones,
    meterInfo,
    meterReaderZone,
    booksZone,
    regions,
    provinces,
    municipalities,
    barangays,
  ] = results.map((result) => result.data || [])

  const form = useForm<z.infer<typeof consumerSchema>>({
    resolver: zodResolver(consumerSchema),
    defaultValues: editData
      ? {
          fullName: editData.full_name || "",
          active: editData.connection_status || "",
          address: {
            region: editData.region,
            province: editData.province,
            municipality: editData.municipality,
            barangay: editData.barangay,
            postalCode: editData.postal_code || "",
            street: editData.street || "",
          },
          latitude: editData.lat || location?.lat,
          longitude: editData.lon || location?.lon,
          mobileNumber: editData.contact_num?.startsWith("+63")
            ? editData.contact_num.slice(3)
            : editData.contact_num || "",
          email: editData.email || "",
          connectionTypes: editData.connection_type || "",
          zone: editData.zone_id,
          books: editData.book_id,
          installationDate: editData.installation_date
            ? new Date(editData.installation_date)
            : new Date(),
          meterReader: editData.reader,
          personnelInstalled: editData.personnel_installed || "",
          serialNumber: editData.serialNumber || "",
          is_sub_to_email_notif: editData.is_sub_to_email_notif,
          is_sub_to_paperless_bill: editData.is_sub_to_paperless_bill,
          meterInfoId: editData.meter_info?.meter_info_id || 0,

          //New Added Data
          cardNumber: "",
          accountNumber: "",
          serviceFees: "",
          orNumber: "",
          consumptionDeposit: "",
          orDate: "",
          inspectionFee: "",
          laborFee: "",
          other: "",
          remarks: "",
        }
      : {
          fullName: "",
          active: "Pending",
          address: {
            region: 0 || Number(JSON.parse(client).region.id),
            province: 0 || Number(JSON.parse(client).province.id),
            municipality: 0,
            barangay: 0,
            postalCode: "",
            street: "",
          },
          latitude: "",
          longitude: "",
          mobileNumber: "",
          email: "",
          connectionTypes: "",
          zone: 0,
          books: 0,
          installationDate: new Date(),
          meterReader: 0,
          personnelInstalled: "",
          serialNumber: "",
          is_sub_to_email_notif: true,
          is_sub_to_paperless_bill: true,
          meterInfoId: 0,

          //New Added Data
          cardNumber: "",
          accountNumber: "",
          serviceFees: "",
          orNumber: "",
          consumptionDeposit: "",
          orDate: "",
          inspectionFee: "",
          laborFee: "",
          other: "",
          remarks: "",
        },
  })

  const mutation = useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id?: number
      payload: SubmitConsumerInfoType
    }) => {
      if (id) {
        await editConsumer(id, payload)
      } else {
        await addConsumer(payload)
      }
    },
    onSuccess: () => {
      toast({
        description: editData
          ? "Consumer updated successfully."
          : "Consumer added successfully.",
      })
      queryClient.invalidateQueries({ queryKey: ["consumerDetails"] })
      queryClient.invalidateQueries({ queryKey: ["consumerInfo"] })
      navigate({ to: "/manage-consumers" })
    },
    onError: () => {
      toast({
        title: "Something went wrong!",
        description: "Please try again.",
        variant: "destructive",
      })
    },
  })

  async function onSubmit(values: z.infer<typeof consumerSchema>) {
    console.log("Submitted")

    const payload: SubmitConsumerInfoType = {
      full_name: values.fullName,
      region_code: values.address.region,
      province_code: values.address.province,
      municipality_code: values.address.municipality,
      barangay_code: values.address.barangay,
      postal_code: values.address.postalCode,
      street: values.address.street,
      contact_num: "+63" + values.mobileNumber,
      email: values.email,
      connection_type: values.connectionTypes,
      connection_status: values.active,
      zone: values.zone,
      book: values.books || 0,
      reader: values.meterReader || 0,
      lat: values.latitude,
      lon: values.longitude,
      is_sub_to_email_notif: values.is_sub_to_email_notif,
      is_sub_to_paperless_bill: values.is_sub_to_paperless_bill,
      meter_info_id: values.meterInfoId || 0,
      installation_date: values.installationDate.toISOString() || null,
      personnel_installed: values.personnelInstalled,
      serialNumber: values.serialNumber,
      client_id: clientId,
      user_id: Number(userId),

      // New Added Data
      card_number: values.cardNumber || "",
      account_number: values.accountNumber || "",
      service_fees: values.serviceFees || "",
      or_number: values.orNumber || "",
      consumption_deposit: values.consumptionDeposit || "",
      or_date: values.orDate || "",
      inspection_fee: values.inspectionFee || "",
      labor_fee: values.laborFee || "",
      other: values.other || "",
      remarks: values.remarks || "",
    }

    mutation.mutate({ id: editData?.id as number, payload })
  }

  const handlePositionChange = (position: [number, number]) => {
    form.setValue("latitude", position[0])
    form.setValue("longitude", position[1])
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="grid grid-cols-1 gap-4 lg:container"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Left Side Column */}
          <div className="col-span-2 space-y-1">
            {/* ==================================================================
                                      Personal Information
            ================================================================== */}
            <Card className="space-y-2 p-4">
              <p className="text-lg font-semibold">Consumer Information</p>
              <DropdownMenuSeparator />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Full Name <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Full Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="mobileNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Mobile Number{" "}
                        <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            className="peer pl-12"
                            placeholder="Mobile Number"
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "")
                              if (value.length <= 10) {
                                field.onChange(value)
                              }
                            }}
                          />
                          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center pl-3 text-sm text-muted-foreground peer-disabled:opacity-50">
                            +63
                          </span>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Email <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">
                        Consumer Status{" "}
                        <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={connectionStatus}
                          placeholder="Select status"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="connectionTypes"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">
                        Connection Type{" "}
                        <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={connectionType}
                          placeholder="Select type"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="zone"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">
                        Zone <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={zones}
                          placeholder="Select zone"
                          onSelectId={(id?: string | number) =>
                            setZoneId(id as number)
                          }
                          idAsValue
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="books"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">
                        Book <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={booksZone}
                          placeholder="Select book"
                          idAsValue
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="meterReader"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">
                        Meter Reader <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={meterReaderZone}
                          placeholder="Select meter reader"
                          idAsValue
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Card>

            {/* ==================================================================
                                             Address
            ================================================================== */}
            <Card className="space-y-2 p-4">
              <p className="text-lg font-semibold">Address</p>
              <DropdownMenuSeparator />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="address.region"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">
                        Region <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={regions}
                          placeholder="Select region"
                          onSelectId={(id?: string | number) =>
                            setRegionId(id as number | undefined)
                          }
                          idAsValue
                          disabled
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.province"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">
                        Province <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={provinces}
                          placeholder="Select province"
                          onSelectId={(id?: string | number) =>
                            setProvinceId(id as number | undefined)
                          }
                          idAsValue
                          disabled
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.municipality"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">
                        Municipality <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={municipalities}
                          placeholder="Select municipality"
                          onSelectId={(id?: string | number) =>
                            setMunicipalityId(id as number | undefined)
                          }
                          idAsValue
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.barangay"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">
                        Barangay <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={barangays}
                          placeholder="Select barangay"
                          idAsValue
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Postal Code <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Postal Code" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.street"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Street, Building, House No.{" "}
                        <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Street, Building, House No."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Card>

            {/* ==================================================================
                                  Notification Subscription
            ================================================================== */}
            <Card className="space-y-2 p-4">
              <p className="text-lg font-semibold">Notification Subscription</p>
              <DropdownMenuSeparator />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="is_sub_to_paperless_bill"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormControl>
                        <div className="flex items-center space-x-2 py-1">
                          <Checkbox
                            id="paperless-bill"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                          <label
                            htmlFor="paperless-bill"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            Subscribe to Paperless Billing?
                          </label>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="is_sub_to_email_notif"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="email-notif"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                        <label
                          htmlFor="terms"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Subscribe to Reminder Notifications?
                        </label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Card>

            {/* ==================================================================
                                      Meter Information
            ================================================================== */}

            <Card className="space-y-2 p-4">
              <p className="text-lg font-semibold">Meter Information</p>
              <DropdownMenuSeparator />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="installationDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">Installation Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <Icons.calendar className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarSingle
                            selected={
                              field.value ? new Date(field.value) : undefined
                            }
                            onSelect={field.onChange}
                            onApply={() => {
                              const popoverTrigger = document.querySelector(
                                '[aria-expanded="true"]',
                              ) as HTMLElement
                              popoverTrigger?.click()
                            }}
                            onCancel={() => {
                              const popoverTrigger = document.querySelector(
                                '[aria-expanded="true"]',
                              ) as HTMLElement
                              popoverTrigger?.click()
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="personnelInstalled"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Personnel Installed</FormLabel>
                      <FormControl>
                        <Input placeholder="Personnel Installed" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="serialNumber"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel className="py-1">Serial Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Select serial number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Card>

            {/* ==================================================================
                                      Billing Information
            ================================================================== */}
            <Card className="space-y-2 p-4">
              <p className="text-lg font-semibold">Billing Information</p>
              <DropdownMenuSeparator />
              <div className="grid grid-cols-1 gap-4 pb-2 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="accountNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account Number</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter account number..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cardNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Card Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter card number..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DropdownMenuSeparator />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Left Side Inputs */}
                <FormField
                  control={form.control}
                  name="serviceFees"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Service Fees</FormLabel>
                      <FormControl>
                        <Input placeholder="Input Field" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="orNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>OR Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Input Field" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="consumptionDeposit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Consumption Deposit</FormLabel>
                      <FormControl>
                        <Input placeholder="Input Field" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="orDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>OR Date</FormLabel>
                      <FormControl>
                        <Input placeholder="Input Field" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="inspectionFee"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Inspection Fee</FormLabel>
                      <FormControl>
                        <Input placeholder="Input Field" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormItem>
                  <FormLabel>Contract 1</FormLabel>
                  <div className="flex items-center gap-2">
                    <Input disabled value="sampletext.pdf" />
                    <Button type="button" variant="outline" size="icon">
                      <Icons.download className="h-4 w-4" />
                    </Button>
                  </div>
                </FormItem>

                <FormField
                  control={form.control}
                  name="laborFee"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Labor (Zone Plumber)</FormLabel>
                      <FormControl>
                        <Input placeholder="Input Field" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormItem>
                  <FormLabel>Contract 2</FormLabel>
                  <div className="flex items-center gap-2">
                    <Input disabled value="sampletext2.pdf" />
                    <Button type="button" variant="outline" size="icon">
                      <Icons.download className="h-4 w-4" />
                    </Button>
                  </div>
                </FormItem>

                <FormField
                  control={form.control}
                  name="other"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Other</FormLabel>
                      <FormControl>
                        <Input placeholder="Input Field" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Total */}
              <div className="mt-4">
                <span className="font-semibold">Total:</span>{" "}
                <span className="text-teal-600">&lt;sumtotal&gt;</span>
              </div>

              {/* Remarks */}
              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="remarks"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Remarks</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={4}
                          placeholder="Enter remarks..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Card>
          </div>

          {/* Rigth Side Column */}
          <div className="space-y-1">
            <Card className="space-y-2 p-4">
              <div className="space-y-6">
                {/* Photo Applicant */}
                <div>
                  <Label
                    htmlFor="orientation"
                    className="mb-2 flex justify-center"
                  >
                    Photo Applicant
                  </Label>
                  <DropdownMenuSeparator />
                  <div className="mb-2 flex justify-center">
                    <img
                      src={
                        file
                          ? file
                          : profile.image
                            ? `${profile.image}?${new Date().getTime()}`
                            : `${profilePlaceholder}`
                      }
                      alt="photo-applicant"
                      className="h-36 w-36 object-cover"
                    />
                  </div>

                  <div className="relative mx-5">
                    <Input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      multiple
                    />
                    <Button variant="outline" className="w-full">
                      Change ID Photo
                    </Button>
                  </div>
                </div>
                {/* Attend Orientation */}
                <div className="flex flex-col justify-between gap-2 md:flex-col lg:flex-row lg:items-center lg:gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox id="orientation" />
                    <Label htmlFor="orientation" className="flex-1">
                      Attend Orientation
                    </Label>
                  </div>
                  <Input value="NULL" className="w-full lg:w-32" />
                </div>

                {/* Documentary Requirement */}
                <FormItem>
                  <FormLabel>Documentary Requirement</FormLabel>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="--------" />
                    </SelectTrigger>
                    <SelectContent>
                      {docsRequirements.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className={`relative flex items-center justify-center py-2 pl-0 pr-0 after:absolute after:bottom-1 after:h-[1px] after:w-[80%] after:bg-gray-300 data-[state=checked]:bg-[#00B7AE] data-[state=checked]:text-white`}
                        >
                          <span className="w-full text-center">
                            {option.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* File upload */}
                  <div
                    className={`border border-dashed hover:border-solid ${selectedFiles.length > 0 ? "pb-5" : "pb-10"} relative w-full rounded-md text-center`}
                    onDrop={(e) => handleFileDrop(e, setSelectedFiles)} // Make sure to call the correct function
                    onDragOver={preventDefaults}
                    onDragEnter={preventDefaults}
                    onDragLeave={preventDefaults}
                  >
                    <input
                      type="file"
                      onChange={(e) => handleFileSelect(e, setSelectedFiles)} // Update the function call to handleFileSelect for the correct file state
                      className="absolute inset-0 z-0 h-full w-full cursor-pointer opacity-0"
                      multiple
                    />

                    {selectedFiles.length > 0 ? (
                      <div className="mx-auto w-full cursor-pointer">
                        {selectedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="m-2 flex cursor-pointer items-center justify-between rounded border-l-4 border-blue-300 bg-blue-50 p-2"
                          >
                            <button
                              type="button"
                              onClick={() => removeSelectedFile(index)}
                              className="z-10 cursor-pointer text-red-500 hover:text-red-700"
                            >
                              <Icons.circleX className="h-4 w-4" />
                            </button>
                            <p className="text-sm text-blue-800">{file.name}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4">
                        <Icons.upload className="mx-auto mb-2 text-primary" />
                        <p>
                          Drop here to attach or{" "}
                          <span className="cursor-pointer text-blue-500 underline">
                            upload
                          </span>
                        </p>
                        <p className="mt-1 text-sm text-gray-400">
                          Max size: 5GB
                        </p>
                      </div>
                    )}
                  </div>
                </FormItem>

                {/* Supporting Documents */}
                <FormItem>
                  <FormLabel>Supporting Documents</FormLabel>
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="barangay" />
                      <Label htmlFor="barangay">Barangay Certification</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="photo" />
                      <Label htmlFor="photo">Photo 2x2</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="application" />
                      <Label htmlFor="application">
                        Accomplished Application Contract
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="receipt" />
                      <Label htmlFor="receipt">Official Receipt</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="waiver" />
                      <Label htmlFor="waiver">
                        Waiver Form (if not the owner)
                      </Label>
                    </div>

                    <p className="mt-2 text-sm font-medium">
                      One of the following: (if not the owner)
                    </p>
                    <div className="space-y-1 pl-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox id="marriage" />
                        <Label htmlFor="marriage">Marriage Certificate</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="donation" />
                        <Label htmlFor="donation">Deed of Donation</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="death" />
                        <Label htmlFor="death">Death Certificate</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="auth" />
                        <Label htmlFor="auth">Authorization Letter</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="other" />
                        <Label htmlFor="other">Other Applicable Document</Label>
                      </div>
                    </div>
                  </div>
                </FormItem>

                {/* Valid ID */}
                <FormItem>
                  <FormLabel>Valid ID</FormLabel>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="--------" />
                    </SelectTrigger>
                    <SelectContent>
                      {validIds.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className={`relative flex items-center justify-center py-2 pl-0 pr-0 after:absolute after:bottom-1 after:h-[1px] after:w-[80%] after:bg-gray-300 data-[state=checked]:bg-[#00B7AE] data-[state=checked]:text-white`}
                        >
                          <span className="w-full text-center">
                            {option.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/*Valid ID File upload */}
                  <div
                    className={`border border-dashed hover:border-solid ${selectedValidIDFiles.length > 0 ? "pb-5" : "pb-10"} relative w-full rounded-md text-center`}
                    onDrop={(e) => handleFileDrop(e, setSelectedValidIDFiles)} // Make sure to call the correct function
                    onDragOver={preventDefaults}
                    onDragEnter={preventDefaults}
                    onDragLeave={preventDefaults}
                  >
                    <input
                      type="file"
                      onChange={(e) =>
                        handleFileSelect(e, setSelectedValidIDFiles)
                      } // Update the function call to handleFileSelect for the correct file state
                      className="absolute inset-0 z-0 h-full w-full cursor-pointer opacity-0"
                      multiple
                    />

                    {selectedValidIDFiles.length > 0 ? (
                      <div className="mx-auto w-full cursor-pointer">
                        {selectedValidIDFiles.map((file, index) => (
                          <div
                            key={index}
                            className="m-2 flex cursor-pointer items-center justify-between rounded border-l-4 border-blue-300 bg-blue-50 p-2"
                          >
                            <button
                              type="button"
                              onClick={() => removeSelectedValidIDFile(index)}
                              className="z-10 cursor-pointer text-red-500 hover:text-red-700"
                            >
                              <Icons.circleX className="h-4 w-4" />
                            </button>
                            <p className="text-sm text-blue-800">{file.name}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4">
                        <Icons.upload className="mx-auto mb-2 text-primary" />
                        <p>
                          Drop here to attach or{" "}
                          <span className="cursor-pointer text-blue-500 underline">
                            upload
                          </span>
                        </p>
                        <p className="mt-1 text-sm text-gray-400">
                          Max size: 5GB
                        </p>
                      </div>
                    )}
                  </div>
                </FormItem>
              </div>
            </Card>

            {/* ==================================================================
                                     Map Location
            ================================================================== */}
            <Card className="space-y-2 p-4">
              <p className="text-lg font-semibold">Location</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-1">
                <FormField
                  control={form.control}
                  name="latitude"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Latitude <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Latitude"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="longitude"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Longitude <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Longitude"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <span>View Map:</span>
              </div>
              <div className="relative">
                <div className="absolute left-3 top-20 z-[1000]">
                  <div
                    className="flex items-center justify-center rounded-lg bg-primary p-1 text-white"
                    onClick={() => {
                      setMaximizeMap(!maximizeMap)
                    }}
                  >
                    {maximizeMap ? (
                      <Minimize className="cursor-pointer" />
                    ) : (
                      <Maximize className="cursor-pointer" />
                    )}
                  </div>
                </div>

                <div
                  className={`${!maximizeMap ? "h-96" : ""} relative z-0 overflow-hidden rounded-lg border`}
                >
                  <MapRenderer
                    picker={{
                      lat: location.lat,
                      lng: location.lon,
                    }}
                    onPositionChange={handlePositionChange}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={mutation.isPending}
            className="w-full md:max-w-[300px]"
          >
            {mutation.isPending && (
              <Icons.loader className="mr-2 animate-spin" />
            )}
            <span>{editData ? "Update Consumer" : "Add Consumer"}</span>
          </Button>
        </div>
      </form>
    </Form>
  )
}
